import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AppHeader from '../components/ui/AppHeader';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { RfqPackage, RfqSubmission, RfqHubFileRelease, TimberSupplierStructureRef, VendorFileRelease, VendorPortalUserPrefs } from '../types';
import { filterNodeSetsForInvite } from '../utils/timberRfqStructureScope';
import { doc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../utils/firebase';
import {
  downloadTimberSupplierCsv,
  openTimberPrintWindow,
  printTimberSupplierToWindow,
  TIMBER_SUPPLIER_POPUP_BLOCKED_MSG,
  timberSupplierExportHasContent,
  timberSupplierExportOptionsForPackage,
  type SupplierTimberExportState,
} from '../utils/supplierTimberExport';
import SupplierQuoteSubmitModal from '../components/vendor/SupplierQuoteSubmitModal';
import SupplierNotBiddingModal from '../components/vendor/SupplierNotBiddingModal';
import VendorQuotePdfModal from '../components/vendor/VendorQuotePdfModal';
import '../components/vendor/SupplierQuoteSubmitModal.css';
import { logVendorPortalActivity } from '../utils/vendorActivityLogger';
import { prepareSupplierQuoteUpload } from '../utils/supplierQuoteUploadAuth';
import {
  subscribeVendorFileReleases,
  threeDDownloadsForPackage,
  triggerThreeDFileDownload,
} from '../utils/vendorFileReleaseService';
import { subscribeRfqHubFileReleases } from '../utils/rfqHubFileReleaseService';
import {
  normalizeRfqPackage,
  normalizeRfqProject,
  normalizeSupplierOrg,
  normalizeWorkType,
  orgHasCapability,
} from '../utils/vendorHelpers';
import {
  ChevronDown,
  ChevronRight,
  Eye,
  HelpCircle,
  LayoutList,
  Map as MapIcon,
  MessageSquare,
} from 'lucide-react';
import VendorPortalHelpModal from '../components/vendor/VendorPortalHelpModal';
import VendorPortalWorkPanel from '../components/vendor/VendorPortalWorkPanel';
import GroupCommentsPopout from '../components/pm/GroupCommentsPopout';
import { VendorPortalSidebarDealContent } from '../components/vendor/VendorPortalSidebarStatusTable';
import { useVendorPortalTour } from '../components/vendor/VendorPortalTour';
import {
  buildVendorPortalDealGroups,
  dealLatestMaterialsBidTotal,
  groupVendorPackagesByStructure,
  latestVendorRequestRound,
  nextOpenQuoteDeadline,
  resolveVendorPortalProjectNumberDisplay,
  resolveVendorPortalSidebarTitle,
  type VendorPortalDealGroup,
  type VendorPortalWorkGroup,
} from '../utils/vendorPortalDealGroups';
import VendorPortalRequestHeader from '../components/vendor/VendorPortalRequestHeader';
import {
  isDealArchivedForSupplier,
  dealHasApprovedQuote,
  workGroupTableStatus,
  dealLostReasonTooltip,
  type VendorPortalWorkGroupTableStatus,
} from '../utils/vendorPortalApprovals';
import {
  buildPublishPreviewDealGroups,
  buildPublishPreviewVendorReleases,
  PUBLISH_PREVIEW_DEAL_ID,
  readVendorPortalPreview,
} from '../utils/vendorPortalPreviewStorage';
import {
  buildTimberQuoteSubmitSpeciesOptions,
} from '../utils/vendorPortalTimberQuotes';
import {
  normalizeQuotedStructures,
  timberQuoteStructurePickerOptions,
  timberQuoteStreamKey,
} from '../utils/timberQuoteStreamKey';
import { activeScopeStructures } from '../utils/rfqPackageArchivedStructures';
import { cncSubmissionHistoryKey } from '../utils/rfqSubmissionHistory';
import {
  inferQuotedSpeciesFromFileName,
  rfqProjectTimberSupplierDisplaySpecies,
  submissionQuotedSpecies,
} from '../utils/timberRfqSuppliers';
import {
  buildVendorPackageVersions,
} from '../utils/vendorPackageVersion';
import { formatQuoteTotal } from '../utils/rfqQuoteTotal';
import {
  subscribeVendorPortalUserPrefs,
} from '../utils/vendorPortalUserPrefs';
import { projectSiteInputFromProjectAndDeal } from '../utils/projectAddressService';
import {
  buildSupplierProductionGroupContext,
  isProductionCommentKey,
  resolveHubProjectForDeal,
} from '../utils/productionCommentAccess';
import { parseSupplierWorkFlags, supplierCanSeeFinancials, supplierPortalRoleLabel } from '../utils/supplierWorkFlags';
import {
  buildSupplierPortalPreviewSearch,
  parseSupplierPortalPreviewRole,
} from '../utils/vendorPortalPreviewQuery';
import {
  resolveVendorPortalAccessDoc,
  vendorPortalDealUnderContract,
  vendorPortalDeepLinkDealId,
  vendorPortalShowProductionComments,
  type VendorPortalCommentMode,
} from '../utils/vendorPortalProductionComments';
import type { GroupCommentContext } from '../utils/groupComments';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './VendorPortalPage.css';

async function resolveExportState(pkg: RfqPackage): Promise<SupplierTimberExportState | null> {
  if (pkg.proposalSnapshot?.nodeSets?.length) {
    return { nodeSets: pkg.proposalSnapshot.nodeSets };
  }
  if (pkg.proposalVersionId) {
    const vref = await getDoc(doc(db, 'proposalBuilders', pkg.dealId, 'versions', pkg.proposalVersionId));
    if (!vref.exists()) return null;
    const snap = (vref.data() as { snapshot?: { nodeSets?: SupplierTimberExportState['nodeSets'] } })?.snapshot;
    if (!snap?.nodeSets?.length) return null;
    return { nodeSets: snap.nodeSets };
  }
  return null;
}

const VendorPortalPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    supplierOrgs,
    rfqProjects,
    rfqPackages,
    rfqSubmissions,
    enhancedDeals,
    timberProjects,
    createRfqSubmission,
    deleteRfqSubmission,
    productionCommentAccess,
  } = useCRM();

  const realIsSupplier = user?.role === 'supplier';
  const [searchParams] = useSearchParams();
  const isPublishPreview = searchParams.get('publishPreview') === '1';
  const publishPreviewReloadToken = searchParams.get('k') ?? '';
  const publishPreviewPayload = useMemo(() => {
    if (!isPublishPreview) return null;
    return readVendorPortalPreview();
  }, [isPublishPreview, publishPreviewReloadToken]);
  const isEmbedPreview = isPublishPreview && !!publishPreviewPayload;

  const viewAsFromUrl = searchParams.get('viewAs')?.trim() || null;
  const viewAsRole = parseSupplierPortalPreviewRole(searchParams.get('as'));
  const [viewAsSupplierOrgId, setViewAsSupplierOrgId] = useState<string | null>(viewAsFromUrl);
  const viewingAsSupplier = !realIsSupplier && !!viewAsSupplierOrgId && !isEmbedPreview;
  const readOnlyPreview = viewingAsSupplier || isEmbedPreview;

  useEffect(() => {
    if (realIsSupplier || isEmbedPreview) return;
    setViewAsSupplierOrgId(viewAsFromUrl);
  }, [realIsSupplier, viewAsFromUrl, isEmbedPreview]);

  const orgId = isEmbedPreview
    ? publishPreviewPayload!.orgId
    : realIsSupplier
      ? user?.supplierOrgId
      : viewAsSupplierOrgId || undefined;

  const myOrg = useMemo(() => {
    if (isEmbedPreview && publishPreviewPayload) {
      return normalizeSupplierOrg({
        id: publishPreviewPayload.orgId,
        name: publishPreviewPayload.orgName,
        createdAt: new Date(),
        capabilities: publishPreviewPayload.workType === 'cnc_quote' ? ['cnc'] : ['timber'],
      });
    }
    return orgId
      ? normalizeSupplierOrg(
          supplierOrgs.find((o) => o.id === orgId) || { id: orgId, name: '', createdAt: new Date() },
        )
      : null;
  }, [isEmbedPreview, publishPreviewPayload, orgId, supplierOrgs]);

  const showTimberTab = orgHasCapability(myOrg, 'timber');
  const showCncTab = orgHasCapability(myOrg, 'cnc');
  const canSeeFinancials = viewingAsSupplier
    ? viewAsRole !== 'employee'
    : isEmbedPreview || supplierCanSeeFinancials(user);
  const [busyPkg, setBusyPkg] = useState<string | null>(null);
  const [selectedPkgIdByProject, setSelectedPkgIdByProject] = useState<Record<string, string>>({});
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [closedProjectsOpen, setClosedProjectsOpen] = useState(false);
  const [vendorReleases, setVendorReleases] = useState<VendorFileRelease[]>([]);
  const [hubFileReleases, setHubFileReleases] = useState<RfqHubFileRelease[]>([]);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [quoteSubmitPkg, setQuoteSubmitPkg] = useState<RfqPackage | null>(null);
  /** Structure the upload is for when the supplier used a per-structure slot. */
  const [quoteSubmitStructure, setQuoteSubmitStructure] = useState<import('../types').TimberSupplierStructureRef | null>(null);
  const [quoteSubmitFallbackStructures, setQuoteSubmitFallbackStructures] = useState<
    import('../types').TimberSupplierStructureRef[]
  >([]);
  const [quotePdfViewer, setQuotePdfViewer] = useState<{ url: string; title: string } | null>(null);

  useEffect(() => {
    if (!quoteSubmitPkg?.dealId) {
      setQuoteSubmitFallbackStructures([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { loadDealProposalStructures } = await import('../utils/loadDealProposalStructures');
      const structures = await loadDealProposalStructures(quoteSubmitPkg.dealId);
      if (!cancelled) setQuoteSubmitFallbackStructures(structures);
    })();
    return () => {
      cancelled = true;
    };
  }, [quoteSubmitPkg?.dealId, quoteSubmitPkg?.id]);

  /** Submissions the upload modal compares against: the package's, narrowed to the slot's structure. */
  const quoteSubmitExistingSubs = useMemo(() => {
    if (!quoteSubmitPkg?.id) return [];
    const forPkg = rfqSubmissions.filter(s => s.rfqPackageId === quoteSubmitPkg.id);
    if (!quoteSubmitStructure?.id) return forPkg;
    return forPkg.filter(s => {
      const tagged = normalizeQuotedStructures(s.quotedStructures);
      return tagged.length === 1 && tagged[0].id === quoteSubmitStructure.id;
    });
  }, [quoteSubmitPkg, quoteSubmitStructure, rfqSubmissions]);

  const quoteSubmitTimberSpeciesOptions = useMemo(() => {
    if (!quoteSubmitPkg?.id || !orgId) return [];
    if (normalizeWorkType(quoteSubmitPkg.workType) === 'cnc_quote') return [];
    const project = rfqProjects.find(p => p.id === quoteSubmitPkg.rfqProjectId);
    if (!project) return [];
    return buildTimberQuoteSubmitSpeciesOptions(project, orgId, quoteSubmitExistingSubs);
  }, [quoteSubmitPkg, orgId, rfqProjects, quoteSubmitExistingSubs]);

  const quoteSubmitStructureOptions = useMemo(() => {
    if (!quoteSubmitPkg?.id || !orgId) return [];
    const project = rfqProjects.find(p => p.id === quoteSubmitPkg.rfqProjectId);
    if (!project) return [];
    return timberQuoteStructurePickerOptions(
      project,
      orgId,
      quoteSubmitPkg,
      quoteSubmitFallbackStructures,
    );
  }, [quoteSubmitPkg, orgId, rfqProjects, quoteSubmitFallbackStructures]);

  // Per-structure slot → lock the quote to that structure. A request-round package
  // covering exactly one structure is likewise tagged without a picker.
  const quoteSubmitLockedStructures = useMemo(() => {
    if (quoteSubmitStructure?.id) return [quoteSubmitStructure];
    if (!quoteSubmitPkg?.rfqRequestId) return undefined;
    const scope = activeScopeStructures(quoteSubmitPkg.scopeStructures, quoteSubmitPkg.archivedStructureIds);
    return scope.length === 1 ? scope : undefined;
  }, [quoteSubmitPkg, quoteSubmitStructure]);
  const quoteSubmitRequestContext = useMemo(() => {
    if (!quoteSubmitPkg?.rfqRequestId) return undefined;
    const bits = [quoteSubmitPkg.requestLabel?.trim()].filter(Boolean) as string[];
    if (quoteSubmitPkg.quoteDeadline instanceof Date) {
      bits.push(`due ${quoteSubmitPkg.quoteDeadline.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`);
    }
    return bits.length ? bits.join(' · ') : undefined;
  }, [quoteSubmitPkg]);

  const quoteSubmitExistingStreamKeys = useMemo(() => {
    if (!quoteSubmitPkg?.id) return [];
    const project = rfqProjects.find(p => p.id === quoteSubmitPkg.rfqProjectId);
    const existing = quoteSubmitExistingSubs.filter(s => !s.notBidding);
    const isCnc = normalizeWorkType(quoteSubmitPkg.workType) === 'cnc_quote';
    return existing.map((s) =>
      isCnc
        ? cncSubmissionHistoryKey(s)
        : timberQuoteStreamKey(s, (sub) => {
            if (!project || !orgId) return sub.quotedSpecies;
            const options = rfqProjectTimberSupplierDisplaySpecies(project, orgId, existing);
            return (
              submissionQuotedSpecies(sub, options)
              ?? inferQuotedSpeciesFromFileName(sub.fileName ?? '', options)
              ?? sub.quotedSpecies
            );
          }),
    );
  }, [quoteSubmitPkg, quoteSubmitExistingSubs, rfqProjects, orgId]);

  const quoteSubmitAllTimberSpeciesQuoted = useMemo(
    () =>
      quoteSubmitTimberSpeciesOptions.length > 0
      && quoteSubmitTimberSpeciesOptions.every(option => option.alreadyQuoted),
    [quoteSubmitTimberSpeciesOptions],
  );
  const [notBiddingModal, setNotBiddingModal] = useState<{
    mode: 'confirm' | 'remove';
    pkg: RfqPackage;
    sub?: RfqSubmission;
    openQuoteAfterRemove?: boolean;
  } | null>(null);
  const [portalPrefs, setPortalPrefs] = useState<VendorPortalUserPrefs | null>(null);
  const [portalPrefsLoaded, setPortalPrefsLoaded] = useState(false);
  const [commentsContext, setCommentsContext] = useState<GroupCommentContext | null>(null);
  const [commentsDealId, setCommentsDealId] = useState<string | null>(null);
  const [commentsToken, setCommentsToken] = useState<number | null>(null);
  const [highlightCommentId, setHighlightCommentId] = useState<string | null>(null);
  const [accessEnsureDone, setAccessEnsureDone] = useState(false);
  const [deepLinkConsumed, setDeepLinkConsumed] = useState(false);

  const deepLinkDealId = vendorPortalDeepLinkDealId({
    dealParam: searchParams.get('deal'),
    groupParam: searchParams.get('group'),
  });
  const deepLinkGroup = searchParams.get('group')?.trim() || '';
  const deepLinkComment = searchParams.get('taskComment')?.trim() || '';

  const commentMode: VendorPortalCommentMode = isEmbedPreview
    ? 'embed'
    : realIsSupplier
      ? 'supplier'
      : 'staff-preview';
  const supplierFlags = parseSupplierWorkFlags(user?.supplierDesignations);

  useEffect(() => {
    if (isEmbedPreview || !orgId || viewingAsSupplier) return;
    void logVendorPortalActivity(orgId, 'page_view', 'vendor_portal_opened');
  }, [orgId, viewingAsSupplier, isEmbedPreview]);

  useEffect(() => {
    if (isEmbedPreview || !user?.id) {
      setPortalPrefs(null);
      setPortalPrefsLoaded(isEmbedPreview);
      return;
    }
    setPortalPrefsLoaded(false);
    return subscribeVendorPortalUserPrefs(
      user.id,
      prefs => {
        setPortalPrefs(prefs);
        setPortalPrefsLoaded(true);
      },
      () => setPortalPrefsLoaded(true),
    );
  }, [user?.id, isEmbedPreview]);

  const liveDealGroups = useMemo(
    () => buildVendorPortalDealGroups(orgId, rfqProjects, rfqPackages),
    [orgId, rfqProjects, rfqPackages],
  );

  const dealGroups = useMemo(
    () =>
      isEmbedPreview && publishPreviewPayload
        ? buildPublishPreviewDealGroups(publishPreviewPayload)
        : liveDealGroups,
    [isEmbedPreview, publishPreviewPayload, liveDealGroups],
  );

  const selectedDeal = useMemo(
    () => dealGroups.find(d => d.dealId === selectedDealId) ?? null,
    [dealGroups, selectedDealId],
  );

  const { startTour } = useVendorPortalTour({
    userId: user?.id,
    orgId,
    showTimber: showTimberTab && !!selectedDeal?.timber,
    showCnc: showCncTab && !!selectedDeal?.cnc,
    projectCount: dealGroups.length,
    readOnlyPreview: readOnlyPreview || isEmbedPreview,
    realIsSupplier: realIsSupplier && !isEmbedPreview,
    prefs: portalPrefs,
    prefsLoaded: portalPrefsLoaded,
    portalReady: !!selectedDeal && (!!selectedDeal.timber || !!selectedDeal.cnc),
  });

  const handleTakeTour = useCallback(() => {
    setShowHelpModal(false);
    startTour({ manual: true });
  }, [startTour]);

  const dealsById = useMemo(
    () => new Map(enhancedDeals.filter(d => d.id).map(d => [d.id as string, d])),
    [enhancedDeals],
  );

  const effectiveDealsById = useMemo(() => {
    if (!isEmbedPreview || !publishPreviewPayload) return dealsById;
    return new Map([
      [
        PUBLISH_PREVIEW_DEAL_ID,
        {
          id: PUBLISH_PREVIEW_DEAL_ID,
          projectNumber: publishPreviewPayload.projectNumber,
          descriptor: publishPreviewPayload.projectName,
          title: publishPreviewPayload.dealTitle,
        },
      ],
    ]);
  }, [isEmbedPreview, publishPreviewPayload, dealsById]);

  const selectedDealProjectSite = useMemo(() => {
    if (isEmbedPreview && publishPreviewPayload?.shipToAddress?.line1?.trim()) {
      const snap = publishPreviewPayload.shipToAddress;
      return {
        location: [snap.line1, snap.city, snap.state, snap.zip].filter(Boolean).join(', '),
        address: {
          street: snap.line1,
          city: snap.city ?? '',
          state: snap.state ?? '',
          zipCode: snap.zip ?? '',
          country: 'United States',
        },
      };
    }
    if (!selectedDealId) return {};
    const deal = dealsById.get(selectedDealId);
    const hubTimberProject =
      timberProjects.find(
        p =>
          p.dealId === selectedDealId
          && (p.phase === 'contracted' || p.phase === undefined || p.phase !== 'preconstruction'),
      ) || timberProjects.find(p => p.dealId === selectedDealId);
    return projectSiteInputFromProjectAndDeal(hubTimberProject, deal);
  }, [isEmbedPreview, publishPreviewPayload, selectedDealId, dealsById, timberProjects]);

  const submissionsByPackage = useMemo(() => {
    const map = new Map<string, RfqSubmission[]>();
    rfqSubmissions.forEach((s) => {
      const list = map.get(s.rfqPackageId) || [];
      list.push(s);
      map.set(s.rfqPackageId, list);
    });
    map.forEach((list) => {
      list.sort((a, b) => {
        const ta = a.createdAt instanceof Date ? a.createdAt.getTime() : 0;
        const tb = b.createdAt instanceof Date ? b.createdAt.getTime() : 0;
        return tb - ta;
      });
    });
    return map;
  }, [rfqSubmissions]);

  const { activeDealGroups, closedDealGroups } = useMemo(() => {
    const active: typeof dealGroups = [];
    const closed: typeof dealGroups = [];
    for (const deal of dealGroups) {
      if (isDealArchivedForSupplier(deal, submissionsByPackage, orgId)) {
        closed.push(deal);
      } else {
        active.push(deal);
      }
    }
    return { activeDealGroups: active, closedDealGroups: closed };
  }, [dealGroups, submissionsByPackage, orgId]);

  const { approvedActiveDealGroups, openActiveDealGroups } = useMemo(() => {
    const approved: typeof activeDealGroups = [];
    const open: typeof activeDealGroups = [];
    for (const deal of activeDealGroups) {
      if (dealHasApprovedQuote(deal, submissionsByPackage, orgId)) approved.push(deal);
      else open.push(deal);
    }
    return { approvedActiveDealGroups: approved, openActiveDealGroups: open };
  }, [activeDealGroups, submissionsByPackage, orgId]);

  useEffect(() => {
    if (!dealGroups.length) {
      setSelectedDealId(null);
      return;
    }
    setSelectedDealId(prev => {
      if (deepLinkDealId && dealGroups.some(d => d.dealId === deepLinkDealId)) {
        return deepLinkDealId;
      }
      if (prev && dealGroups.some(d => d.dealId === prev)) return prev;
      return (
        approvedActiveDealGroups[0]?.dealId
        ?? openActiveDealGroups[0]?.dealId
        ?? activeDealGroups[0]?.dealId
        ?? dealGroups[0].dealId
      );
    });
  }, [dealGroups, activeDealGroups, approvedActiveDealGroups, openActiveDealGroups, deepLinkDealId]);

  const selectedClosedDealId = closedDealGroups.some(d => d.dealId === selectedDealId)
    ? selectedDealId
    : null;

  useEffect(() => {
    if (selectedClosedDealId) setClosedProjectsOpen(true);
  }, [selectedClosedDealId]);

  useEffect(() => {
    setSelectedPkgIdByProject(prev => {
      const next = { ...prev };
      let changed = false;
      for (const deal of dealGroups) {
        for (const work of [deal.timber, deal.cnc]) {
          if (!work?.project.id || !work.packages.length) continue;
          const versions = buildVendorPackageVersions(work.packages, submissionsByPackage);
          const latest = versions.find(v => v.isLatest) || versions[0];
          const latestId = latest?.pkg.id;
          if (!latestId) continue;
          const current = prev[work.project.id];
          const currentExists = current && versions.some(v => v.pkg.id === current);
          if (!currentExists) {
            next[work.project.id] = latestId;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [dealGroups, submissionsByPackage]);

  const cncProjectIdForReleases = selectedDeal?.cnc?.project.id ?? null;
  const timberProjectIdForReleases = selectedDeal?.timber?.project.id ?? null;

  const hubReleaseById = useMemo(
    () => new Map(hubFileReleases.filter(r => r.id).map(r => [r.id as string, r])),
    [hubFileReleases],
  );

  const publishPreviewVendorReleases = useMemo(
    () => (publishPreviewPayload ? buildPublishPreviewVendorReleases(publishPreviewPayload) : []),
    [publishPreviewPayload],
  );

  useEffect(() => {
    if (isEmbedPreview) {
      setVendorReleases(publishPreviewVendorReleases);
      return;
    }
    if (!cncProjectIdForReleases || !orgId) {
      setVendorReleases([]);
      return;
    }
    return subscribeVendorFileReleases(
      cncProjectIdForReleases,
      orgId,
      setVendorReleases,
      (err) => console.warn('[VendorPortal] vendorFileReleases listen failed:', err),
    );
  }, [isEmbedPreview, publishPreviewVendorReleases, cncProjectIdForReleases, orgId]);

  useEffect(() => {
    if (isEmbedPreview) {
      setHubFileReleases([]);
      return;
    }
    const ids = [timberProjectIdForReleases, cncProjectIdForReleases].filter(
      (id, index, all): id is string => !!id && all.indexOf(id) === index,
    );
    if (!ids.length) {
      setHubFileReleases([]);
      return;
    }
    const buckets = new Map<string, RfqHubFileRelease[]>();
    const unsubs = ids.map((id) =>
      subscribeRfqHubFileReleases(
        id,
        (rows) => {
          buckets.set(id, rows);
          setHubFileReleases(ids.flatMap((projectId) => buckets.get(projectId) ?? []));
        },
        (err) => console.warn('[VendorPortal] hubFileReleases listen failed:', err),
      ),
    );
    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [isEmbedPreview, timberProjectIdForReleases, cncProjectIdForReleases]);

  const logAction = useCallback(
    (action: string, pkg?: RfqPackage, meta?: Record<string, unknown>) => {
      if (!orgId || viewingAsSupplier) return;
      void logVendorPortalActivity(orgId, 'download', action, {
        rfqProjectId: pkg?.rfqProjectId,
        rfqPackageId: pkg?.id,
        metadata: meta,
      });
    },
    [orgId, viewingAsSupplier],
  );

  /** Export state narrowed to one structure when the supplier downloads per structure. */
  const exportStateForStructure = useCallback(
    (
      state: SupplierTimberExportState,
      structure?: TimberSupplierStructureRef,
      pkg?: RfqPackage,
    ): SupplierTimberExportState | null => {
      const remaining = structure
        ? [structure]
        : pkg?.archivedStructureIds?.length
          ? activeScopeStructures(pkg.scopeStructures, pkg.archivedStructureIds)
          : null;
      if (!remaining) return state;
      if (!remaining.length) return null;
      const nodeSets = filterNodeSetsForInvite(state.nodeSets, {
        structureScope: 'selected',
        structures: remaining,
      });
      return nodeSets.length ? { ...state, nodeSets } : null;
    },
    [],
  );

  const handleDownloadCsv = useCallback(
    async (pkg: RfqPackage, structure?: TimberSupplierStructureRef) => {
      setBusyPkg(pkg.id || '');
      try {
        const full = await resolveExportState(pkg);
        const state = full ? exportStateForStructure(full, structure, pkg) : null;
        if (!state) {
          alert(
            structure
              ? `Could not find ${structure.name || 'that structure'} in this package's timber list.`
              : 'Could not load timber data for this package.',
          );
          return;
        }
        const project = rfqProjects.find(p => p.id === pkg.rfqProjectId);
        const exportOptions = timberSupplierExportOptionsForPackage(project, pkg, state.nodeSets);
        const base = `timber-rfq-${pkg.dealTitle || pkg.dealId}${structure?.name ? `-${structure.name}` : ''}`;
        downloadTimberSupplierCsv(state, base, exportOptions);
        logAction('download_timber_csv', pkg, structure ? { structureId: structure.id, structureName: structure.name } : undefined);
      } finally {
        setBusyPkg(null);
      }
    },
    [exportStateForStructure, logAction, rfqProjects],
  );

  const handleDownloadPdf = useCallback(
    async (pkg: RfqPackage, structure?: TimberSupplierStructureRef) => {
      const printWindow = openTimberPrintWindow();
      if (!printWindow) {
        alert(TIMBER_SUPPLIER_POPUP_BLOCKED_MSG);
        return;
      }
      printWindow.document.open();
      printWindow.document.write('<p style="font-family:sans-serif;padding:24px">Loading timber list…</p>');
      printWindow.document.close();

      setBusyPkg(pkg.id || '');
      try {
        const full = await resolveExportState(pkg);
        const state = full ? exportStateForStructure(full, structure, pkg) : null;
        if (!state) {
          printWindow.close();
          alert(
            structure
              ? `Could not find ${structure.name || 'that structure'} in this package's timber list.`
              : 'Could not load timber data for this package.',
          );
          return;
        }
        if (!timberSupplierExportHasContent(state)) {
          printWindow.close();
          alert('This package has no timber lines to export.');
          return;
        }
        const project = rfqProjects.find(p => p.id === pkg.rfqProjectId);
        const exportOptions = timberSupplierExportOptionsForPackage(project, pkg, state.nodeSets);
        const title = structure?.name ? `${pkg.dealTitle || ''} — ${structure.name}`.replace(/^ — /, '') : pkg.dealTitle;
        await printTimberSupplierToWindow(printWindow, state, title, undefined, exportOptions);
        logAction('download_timber_pdf', pkg, structure ? { structureId: structure.id, structureName: structure.name } : undefined);
      } catch (e) {
        console.error(e);
        try {
          printWindow.close();
        } catch {
          /* ignore */
        }
        alert(e instanceof Error ? e.message : 'Failed to open timber PDF.');
      } finally {
        setBusyPkg(null);
      }
    },
    [exportStateForStructure, logAction, rfqProjects],
  );

  const handleViewSubmission = useCallback(async (s: RfqSubmission) => {
    if (!canSeeFinancials) return;
    if (!s.storagePath) return;
    try {
      const url = await getDownloadURL(ref(storage, s.storagePath));
      const fileName = s.fileName || 'Quote';
      const isPdf =
        /\.pdf$/i.test(fileName) || (s.contentType || '').toLowerCase().includes('pdf');
      if (isPdf) {
        setQuotePdfViewer({ url, title: fileName });
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      alert('Could not open the file.');
    }
  }, [canSeeFinancials]);

  const handleUpload = useCallback(
    async (
      pkg: RfqPackage,
      file: File | null,
      opts: {
        quoteTotal: number;
        leadTimeWeeksMin?: number;
        leadTimeWeeksMax?: number;
        notes?: string;
        quotedSpecies?: string;
        quotedDryness?: RfqSubmission['quotedDryness'];
        quotedStructures?: RfqSubmission['quotedStructures'];
      },
    ) => {
      if (!canSeeFinancials) return;
      if (readOnlyPreview) {
        alert('Preview mode — switch to the supplier account to submit quotes.');
        return;
      }
      if (!user?.id || !pkg.id) return;
      if (!file) return;
      if (!opts.quoteTotal || opts.quoteTotal <= 0) {
        alert('Enter a valid total quote amount.');
        return;
      }
      const orgFromUser = (user.supplierOrgId || '').trim();
      const orgFromPkg = (pkg.supplierOrgId || '').trim();
      if (!orgFromUser || !orgFromPkg || orgFromUser !== orgFromPkg) {
        alert('This package is not assigned to your organization.');
        return;
      }
      const existing = rfqSubmissions.filter((s) => s.rfqPackageId === pkg.id);
      if (existing.some((s) => s.notBidding)) {
        return;
      }

      await prepareSupplierQuoteUpload(orgFromUser);

      let storagePath = '';
      let downloadUrl: string | undefined;
      let fileName = file?.name || 'CNC quote';
      let contentType: string | undefined;
      let sizeBytes: number | undefined;

      if (file) {
        const path = `supplier-rfq-quotes/${orgFromUser}/${pkg.id}/${Date.now()}_${file.name.replace(/[^\w.\-]+/g, '_')}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, file, { contentType: file.type || 'application/octet-stream' });
        storagePath = path;
        downloadUrl = await getDownloadURL(storageRef);
        fileName = file.name;
        contentType = file.type;
        sizeBytes = file.size;
      }

      await createRfqSubmission({
        rfqPackageId: pkg.id,
        rfqProjectId: pkg.rfqProjectId,
        supplierOrgId: orgFromUser,
        dealId: pkg.dealId,
        workType: normalizeWorkType(pkg.workType),
        fileName,
        storagePath,
        downloadUrl,
        contentType,
        sizeBytes,
        quoteTotal: opts.quoteTotal,
        leadTimeWeeksMin: opts.leadTimeWeeksMin,
        leadTimeWeeksMax: opts.leadTimeWeeksMax,
        notes: opts.notes,
        quotedSpecies: opts.quotedSpecies,
        quotedDryness: opts.quotedDryness,
        quotedStructures: opts.quotedStructures?.length ? opts.quotedStructures : undefined,
        uploadedByUid: user.id,
        uploadedByName: user.name || user.email,
      });

      void logVendorPortalActivity(orgFromUser, 'upload', 'quote_submitted', {
        rfqProjectId: pkg.rfqProjectId,
        rfqPackageId: pkg.id,
        metadata: { workType: pkg.workType, hasFile: !!file },
      });
    },
    [user, createRfqSubmission, rfqSubmissions, readOnlyPreview, canSeeFinancials],
  );

  const requestNotBidding = useCallback(
    (pkg: RfqPackage) => {
      if (!canSeeFinancials) return;
      if (readOnlyPreview) {
        alert('Preview mode — switch to the supplier account to submit responses.');
        return;
      }
      if (!user?.id || !pkg.id) return;
      const orgFromUser = (user.supplierOrgId || '').trim();
      const orgFromPkg = (pkg.supplierOrgId || '').trim();
      if (!orgFromUser || !orgFromPkg || orgFromUser !== orgFromPkg) {
        alert('This package is not assigned to your organization.');
        return;
      }
      const existing = rfqSubmissions.filter((s) => s.rfqPackageId === pkg.id);
      if (existing.some((s) => s.notBidding)) {
        const sub = existing.find((s) => s.notBidding);
        if (sub) {
          setNotBiddingModal({ mode: 'remove', pkg, sub });
        }
        return;
      }
      if (existing.some((s) => s.storagePath && !s.notBidding) || existing.some((s) => s.cncQuote)) {
        alert('Delete your existing response first.');
        return;
      }
      setNotBiddingModal({ mode: 'confirm', pkg });
    },
    [user, rfqSubmissions, readOnlyPreview, canSeeFinancials],
  );

  const requestRemoveNotBidding = useCallback(
    (pkg: RfqPackage, sub: RfqSubmission, openQuoteAfterRemove = false) => {
      if (readOnlyPreview) {
        alert('Preview mode — switch to the supplier account to manage responses.');
        return;
      }
      if (!user?.id || sub.uploadedByUid !== user.id || !sub.notBidding) return;
      setNotBiddingModal({ mode: 'remove', pkg, sub, openQuoteAfterRemove });
    },
    [user, readOnlyPreview],
  );

  const handleNotBiddingModalConfirm = useCallback(async () => {
    if (!notBiddingModal || !user?.id) return;
    const { mode, pkg, sub, openQuoteAfterRemove } = notBiddingModal;
    if (!pkg.id) return;

    const orgFromUser = (user.supplierOrgId || '').trim();
    const orgFromPkg = (pkg.supplierOrgId || '').trim();
    if (!orgFromUser || !orgFromPkg || orgFromUser !== orgFromPkg) {
      alert('This package is not assigned to your organization.');
      return;
    }

    setBusyPkg(pkg.id);
    try {
      if (mode === 'confirm') {
        await createRfqSubmission({
          rfqPackageId: pkg.id,
          rfqProjectId: pkg.rfqProjectId,
          supplierOrgId: orgFromUser,
          dealId: pkg.dealId,
          workType: normalizeWorkType(pkg.workType),
          fileName: 'Not bidding',
          storagePath: '',
          notBidding: true,
          uploadedByUid: user.id,
          uploadedByName: user.name || user.email,
        });
        setNotBiddingModal(null);
      } else if (sub?.id) {
        await deleteRfqSubmission({
          id: sub.id,
          storagePath: sub.storagePath,
          notBidding: sub.notBidding,
        });
        setNotBiddingModal(null);
        if (openQuoteAfterRemove) {
          setQuoteSubmitPkg(pkg);
        }
      }
    } finally {
      setBusyPkg(null);
    }
  }, [notBiddingModal, user, createRfqSubmission, deleteRfqSubmission]);

  const handleUploadQuoteRequest = useCallback(
    (pkg: RfqPackage, structure?: import('../types').TimberSupplierStructureRef) => {
      if (!canSeeFinancials) return;
      const existing = rfqSubmissions.filter((s) => s.rfqPackageId === pkg.id);
      const notBiddingSub = existing.find((s) => s.notBidding);
      if (notBiddingSub) {
        requestRemoveNotBidding(pkg, notBiddingSub, true);
        return;
      }
      setQuoteSubmitStructure(structure ?? null);
      setQuoteSubmitPkg(pkg);
    },
    [rfqSubmissions, requestRemoveNotBidding, canSeeFinancials],
  );

  const handleDeleteSubmission = useCallback(
    async (pkg: RfqPackage, sub: RfqSubmission) => {
      if (!canSeeFinancials) return;
      if (readOnlyPreview) {
        alert('Preview mode — switch to the supplier account to manage responses.');
        return;
      }
      if (!user?.id || sub.uploadedByUid !== user.id) return;
      if (sub.notBidding) {
        requestRemoveNotBidding(pkg, sub);
        return;
      }
      if (!window.confirm('Remove this response?')) return;
      await deleteRfqSubmission({
        id: sub.id!,
        storagePath: sub.storagePath,
        notBidding: sub.notBidding,
      });
    },
    [user, deleteRfqSubmission, readOnlyPreview, requestRemoveNotBidding, canSeeFinancials],
  );

  const handleDownloadThreeDFile = useCallback(
    async (pkg: RfqPackage) => {
      setBusyPkg(pkg.id || '');
      try {
        const releases = threeDDownloadsForPackage(pkg, vendorReleases, hubFileReleases, orgId);
        const release = releases[0];
        if (!release || !triggerThreeDFileDownload(release)) {
          alert('No 3D model is available for this package.');
          return;
        }
        logAction('download_3d_model', pkg, { releaseId: release.id, fileName: release.fileName });
      } finally {
        setBusyPkg(null);
      }
    },
    [vendorReleases, hubFileReleases, orgId, logAction],
  );

  const dealIdsKey = useMemo(
    () => dealGroups.map(deal => deal.dealId).filter(Boolean).join(','),
    [dealGroups],
  );

  const accessForDeal = useCallback(
    (dealId: string) =>
      resolveVendorPortalAccessDoc({
        dealId,
        storedAccess: productionCommentAccess,
        rfqProjects,
        submissions: rfqSubmissions,
        hubProjects: timberProjects,
        requireStored: realIsSupplier,
      }),
    [productionCommentAccess, rfqProjects, rfqSubmissions, timberProjects, realIsSupplier],
  );

  const openProductionComments = useCallback(
    (dealId: string, highlightId?: string | null) => {
      const access = accessForDeal(dealId);
      setCommentsDealId(dealId);
      setCommentsContext(
        buildSupplierProductionGroupContext({
          dealId,
          hubProjectId: access.hubProjectId,
        }),
      );
      setHighlightCommentId(highlightId || null);
      setCommentsToken(Date.now());
    },
    [accessForDeal],
  );

  const supplierViewAsSuffix =
    viewingAsSupplier && viewAsSupplierOrgId
      ? buildSupplierPortalPreviewSearch({ orgId: viewAsSupplierOrgId, role: viewAsRole })
      : '';

  const hubProjectIdForDeal = useCallback(
    (dealId: string): string | null => {
      const fromAccess = accessForDeal(dealId).hubProjectId?.trim();
      if (fromAccess) return fromAccess;
      return resolveHubProjectForDeal(timberProjects, dealId)?.id?.trim() || null;
    },
    [accessForDeal, timberProjects],
  );

  const dealCanOpenProductionComments = useCallback(
    (deal: VendorPortalDealGroup): boolean => {
      const access = accessForDeal(deal.dealId);
      const timberOk = !!(
        deal.timber
        && vendorPortalShowProductionComments({
          workType: 'timber_rfq',
          orgId,
          flags: supplierFlags,
          orgCapabilities: myOrg?.capabilities ?? [],
          access,
          mode: commentMode,
        })
      );
      const cncOk = !!(
        deal.cnc
        && vendorPortalShowProductionComments({
          workType: 'cnc_quote',
          orgId,
          flags: supplierFlags,
          orgCapabilities: myOrg?.capabilities ?? [],
          access,
          mode: commentMode,
        })
      );
      return timberOk || cncOk;
    },
    [accessForDeal, commentMode, myOrg?.capabilities, orgId, supplierFlags],
  );

  const openDealProductionDetails = useCallback(
    (dealId: string) => {
      const projectId = hubProjectIdForDeal(dealId);
      if (!projectId) return;
      navigate(`/project-hub/${projectId}${supplierViewAsSuffix}`);
    },
    [hubProjectIdForDeal, navigate, supplierViewAsSuffix],
  );

  const renderSidebarDealItem = useCallback(
    (
      deal: VendorPortalDealGroup,
      timberTableStatus: VendorPortalWorkGroupTableStatus | null,
      cncTableStatus: VendorPortalWorkGroupTableStatus | null,
      opts?: { lostReason?: string | null },
    ) => {
      const projectNumber = resolveVendorPortalProjectNumberDisplay(
        deal.dealId,
        deal.title,
        effectiveDealsById,
      );
      const sidebarTitle = resolveVendorPortalSidebarTitle(
        deal.dealId,
        deal.title,
        effectiveDealsById,
        projectNumber,
      );
      const materialsBidTotal = canSeeFinancials
        ? dealLatestMaterialsBidTotal(deal, submissionsByPackage, {
            includeTimber: showTimberTab,
            includeCnc: showCncTab,
          })
        : null;
      const isSelected = deal.dealId === selectedDealId;
      const isApproved =
        timberTableStatus === 'approved' || cncTableStatus === 'approved';
      const quoteDueDate = deal.timber ? nextOpenQuoteDeadline(deal.timber.packages, submissionsByPackage) : null;
      const showComments = isApproved && dealCanOpenProductionComments(deal);
      const hubProjectId = isApproved ? hubProjectIdForDeal(deal.dealId) : null;
      const showDetails = isApproved && !!hubProjectId;
      const lostReason = (opts?.lostReason || '').trim() || null;

      return (
        <li key={deal.dealId}>
          <div
            className={`vp-sidebar-item${isSelected ? ' active' : ''}${isApproved ? ' vp-sidebar-item--approved' : ''}${lostReason ? ' vp-sidebar-item--has-lost-reason' : ''}`}
          >
            {lostReason && (
              <div className="vp-sidebar-lost-tip" role="tooltip">
                <span className="vp-sidebar-lost-tip-label">Lost reason</span>
                <span className="vp-sidebar-lost-tip-body">{lostReason}</span>
              </div>
            )}
            <button
              type="button"
              className="vp-sidebar-item-select"
              onClick={() => setSelectedDealId(deal.dealId)}
              aria-current={isSelected ? 'true' : undefined}
              aria-label={
                lostReason
                  ? `${sidebarTitle || 'Project'}. Lost reason: ${lostReason}`
                  : undefined
              }
            >
              <VendorPortalSidebarDealContent
                projectNumber={projectNumber}
                sidebarTitle={sidebarTitle}
                materialsBidTotal={materialsBidTotal}
                timberStatus={timberTableStatus}
                cncStatus={cncTableStatus}
                showTimber={showTimberTab}
                showCnc={showCncTab}
                formatBidTotal={formatQuoteTotal}
                quoteDueDate={quoteDueDate}
              />
            </button>
            {(showComments || showDetails) && (
              <div className="vp-sidebar-item-actions">
                {showComments && (
                  <button
                    type="button"
                    className="vp-sidebar-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openProductionComments(deal.dealId);
                    }}
                    title="Open Production comments"
                    aria-label={`Open Production comments for ${sidebarTitle}`}
                  >
                    <MessageSquare size={13} strokeWidth={1.85} aria-hidden />
                    Comments
                  </button>
                )}
                {showDetails && (
                  <button
                    type="button"
                    className="vp-sidebar-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDealProductionDetails(deal.dealId);
                    }}
                    title="Open production task list"
                    aria-label={`Open production details for ${sidebarTitle}`}
                  >
                    <LayoutList size={13} strokeWidth={1.85} aria-hidden />
                    Details
                  </button>
                )}
              </div>
            )}
          </div>
        </li>
      );
    },
    [
      dealCanOpenProductionComments,
      effectiveDealsById,
      hubProjectIdForDeal,
      openDealProductionDetails,
      openProductionComments,
      canSeeFinancials,
      selectedDealId,
      showCncTab,
      showTimberTab,
      submissionsByPackage,
    ],
  );

  useEffect(() => {
    if (isEmbedPreview || !orgId || (!realIsSupplier && !viewingAsSupplier)) {
      setAccessEnsureDone(true);
      return;
    }
    const dealIds = dealIdsKey ? dealIdsKey.split(',').slice(0, 40) : [];
    if (!dealIds.length) {
      setAccessEnsureDone(true);
      return;
    }
    let cancelled = false;
    const fn = httpsCallable<{ dealIds: string[] }, { ok: boolean }>(
      getFunctions(undefined, 'us-central1'),
      'ensureProductionCommentAccess',
    );
    void fn({ dealIds })
      .catch((err) => console.warn('[VendorPortal] ensureProductionCommentAccess failed', err))
      .finally(() => {
        if (!cancelled) setAccessEnsureDone(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isEmbedPreview, orgId, realIsSupplier, viewingAsSupplier, dealIdsKey]);

  useEffect(() => {
    if (deepLinkConsumed || !accessEnsureDone) return;
    if (!deepLinkDealId && !deepLinkGroup && !deepLinkComment) return;
    const dealId = deepLinkDealId;
    if (!dealId || !dealGroups.some(d => d.dealId === dealId)) return;
    if (deepLinkGroup && !isProductionCommentKey(deepLinkGroup) && deepLinkGroup !== 'production') {
      setDeepLinkConsumed(true);
      return;
    }
    const access = accessForDeal(dealId);
    const canOpenTimber = vendorPortalShowProductionComments({
      workType: 'timber_rfq',
      orgId,
      flags: supplierFlags,
      orgCapabilities: myOrg?.capabilities ?? [],
      access,
      mode: commentMode,
    });
    const canOpenCnc = vendorPortalShowProductionComments({
      workType: 'cnc_quote',
      orgId,
      flags: supplierFlags,
      orgCapabilities: myOrg?.capabilities ?? [],
      access,
      mode: commentMode,
    });
    if (!canOpenTimber && !canOpenCnc) return;

    // Prefer Project Hub so suppliers (incl. employees) land on the Production
    // thread with the comment highlighted — same surface they use day-to-day.
    const hubProjectId = hubProjectIdForDeal(dealId);
    if (hubProjectId) {
      const params = new URLSearchParams(
        supplierViewAsSuffix.startsWith('?') ? supplierViewAsSuffix.slice(1) : '',
      );
      params.set('group', deepLinkGroup || `${dealId}::production`);
      if (deepLinkComment) params.set('taskComment', deepLinkComment);
      setDeepLinkConsumed(true);
      navigate(`/project-hub/${hubProjectId}?${params.toString()}`, { replace: true });
      return;
    }

    openProductionComments(dealId, deepLinkComment || null);
    setDeepLinkConsumed(true);
  }, [
    accessEnsureDone,
    accessForDeal,
    commentMode,
    dealGroups,
    deepLinkComment,
    deepLinkConsumed,
    deepLinkDealId,
    deepLinkGroup,
    hubProjectIdForDeal,
    myOrg?.capabilities,
    navigate,
    openProductionComments,
    orgId,
    supplierFlags,
    supplierViewAsSuffix,
  ]);

  const selectedDealAccess = selectedDealId ? accessForDeal(selectedDealId) : null;
  const selectedDealUnderContract = selectedDealId
    ? vendorPortalDealUnderContract({
        dealId: selectedDealId,
        storedAccess: selectedDealAccess,
        hubProjects: timberProjects,
        mode: commentMode,
      })
    : false;

  const workPanelCommonProps = {
    orgId,
    supplierOrgName: myOrg?.name || 'Supplier',
    submissionsByPackage,
    vendorReleases,
    hubReleaseById,
    busyPkg,
    readOnlyPreview,
    canSeeFinancials,
    userId: user?.id,
    onDownloadCsv: (pkg: RfqPackage, structure?: TimberSupplierStructureRef) => void handleDownloadCsv(pkg, structure),
    onDownloadPdf: (pkg: RfqPackage, structure?: TimberSupplierStructureRef) => void handleDownloadPdf(pkg, structure),
    onDownloadThreeDFile: (pkg: RfqPackage) => void handleDownloadThreeDFile(pkg),
    onNotBidding: (pkg: RfqPackage) => requestNotBidding(pkg),
    onUploadQuote: handleUploadQuoteRequest,
    onViewSubmission: (sub: RfqSubmission) => void handleViewSubmission(sub),
    onDeleteSubmission: (pkg: RfqPackage, sub: RfqSubmission) => void handleDeleteSubmission(pkg, sub),
  };

  /**
   * One panel per structure set the supplier was sent (Barn, Porch, …). Whole-
   * project packages from before the hub split are shared history inside each
   * lane. A single lane renders exactly as before — no structure suffix.
   */
  const renderStructureWorkPanels = (
    baseTitle: string,
    work: VendorPortalWorkGroup,
    siblingWork: VendorPortalWorkGroup | null,
  ) => {
    const projectId = work.project.id;
    const lanes = groupVendorPackagesByStructure(work.packages);
    const multi = lanes.length > 1;
    // Files → RFQs request round: one header for the newest round across all lanes.
    const latestRound = latestVendorRequestRound(work.packages, submissionsByPackage);
    const roundHeader = latestRound ? (
      <VendorPortalRequestHeader key={`${projectId}-request`} round={latestRound} />
    ) : null;
    const panels = lanes.map(lane => {
      const laneWork: VendorPortalWorkGroup = multi ? { project: work.project, packages: lane.packages } : work;
      const selectionKey = multi && projectId ? `${projectId}::${lane.key}` : projectId || '';
      const selectedPackageId = selectionKey
        ? selectedPkgIdByProject[selectionKey] ||
          buildVendorPackageVersions(laneWork.packages, submissionsByPackage).find(v => v.isLatest)?.pkg.id ||
          laneWork.packages[0]?.id ||
          null
        : null;
      return (
        <div
          key={lane.key}
          className={`vp-structure-lane${multi ? ' vp-structure-lane--multi' : ''}`}
          data-structure-key={lane.key}
        >
          <VendorPortalWorkPanel
            sectionTitle={multi ? `${baseTitle} · ${lane.label}` : baseTitle}
            work={laneWork}
            projectSite={selectedDealProjectSite}
            siblingWork={siblingWork}
            selectedPackageId={selectedPackageId}
            onSelectPackage={packageId => {
              if (!selectionKey) return;
              setSelectedPkgIdByProject(prev => ({ ...prev, [selectionKey]: packageId }));
            }}
            {...workPanelCommonProps}
            underContract={selectedDealUnderContract}
          />
        </div>
      );
    });
    return roundHeader ? [roundHeader, ...panels] : panels;
  };

  return (
    <div className={`vp-page ops-hub${isEmbedPreview ? ' vp-page--embed' : ''}`}>
      {!isEmbedPreview && (
        <AppHeader
          supplierNavPreviewOrgId={viewingAsSupplier ? viewAsSupplierOrgId : null}
          supplierNavPreviewRole={viewingAsSupplier ? viewAsRole : null}
        />
      )}
      <div className="vp-wrap">
        {!isEmbedPreview && (
        <header className="vp-hero ops-hub-toolbar">
          <div className="ops-hub-toolbar-left">
            <h1 className="ops-hub-title vp-org-title">{myOrg?.name || 'Vendor Portal'}</h1>
            <p className="vp-sub">
              {viewingAsSupplier
                ? viewAsRole === 'employee'
                  ? 'Previewing the supplier employee portal — quote prices and submission are hidden.'
                  : 'Previewing this supplier’s owner portal. Quote submission is disabled in preview mode.'
                : 'Review packages, download timber lists, and submit quotes for LC Timber Frames projects.'}
            </p>
          </div>
          <div className="ops-hub-toolbar-right">
            <button
              type="button"
              className="ops-view-btn vp-help-btn"
              onClick={() => handleTakeTour()}
              disabled={!orgId || dealGroups.length === 0}
            >
              <MapIcon size={14} /> Take a tour
            </button>
            <button
              type="button"
              className="ops-view-btn vp-help-btn"
              data-tour="vp-help-btn"
              onClick={() => setShowHelpModal(true)}
            >
              <HelpCircle size={14} /> Help
            </button>
          </div>
        </header>
        )}

        {isEmbedPreview && publishPreviewPayload && (
          <div className="vp-preview-banner vp-preview-banner--embed">
            <Eye size={14} aria-hidden />
            <span>
              Staff preview — <strong>{publishPreviewPayload.orgName}</strong> (unpublished package)
            </span>
          </div>
        )}

        {viewingAsSupplier && orgId && (
          <div className="vp-preview-banner">
            <Eye size={14} aria-hidden />
            <span>
              Previewing as <strong>{myOrg?.name}</strong>
              {' · '}
              {supplierPortalRoleLabel(viewAsRole)}
              {viewAsRole === 'employee'
                ? ' — quote prices hidden.'
                : ' — quote submission is disabled.'}
            </span>
            <Link to="/vendor-admin" className="vp-preview-banner-link">
              Back to Supplier Portal
            </Link>
          </div>
        )}

        {realIsSupplier && !orgId && (
          <p className="vp-banner warn">Your account is not linked to a vendor organization. Contact LC Timber Frames.</p>
        )}

        {!realIsSupplier && !orgId && (
          <p className="vp-banner info">
            Open <Link to="/external-portals">External Portals</Link> under Projects to preview a supplier login.
          </p>
        )}

        {orgId && dealGroups.length > 0 && (
          <div className="vp-shell">
            {selectedDeal && (
              <header className="vp-main-head vp-shell-title">
                <h2 className="vp-main-title">{selectedDeal.title}</h2>
              </header>
            )}

            <aside className="vp-sidebar" aria-label="Projects" data-tour="vp-sidebar">
              <p className="vp-sidebar-heading">Projects</p>
              {approvedActiveDealGroups.length > 0 && (
                <>
                  <p className="vp-sidebar-subheading">
                    <span>Approved</span>
                    <span className="vp-sidebar-subheading-count">{approvedActiveDealGroups.length}</span>
                  </p>
                  <ul className="vp-sidebar-list vp-sidebar-list--approved">
                    {approvedActiveDealGroups.map(deal => {
                      const timberTableStatus =
                        deal.timber && showTimberTab
                          ? workGroupTableStatus(deal.timber, submissionsByPackage, orgId)
                          : null;
                      const cncTableStatus =
                        deal.cnc && showCncTab
                          ? workGroupTableStatus(deal.cnc, submissionsByPackage, orgId)
                          : null;
                      return renderSidebarDealItem(deal, timberTableStatus, cncTableStatus);
                    })}
                  </ul>
                </>
              )}
              {openActiveDealGroups.length > 0 && (
                <>
                  {approvedActiveDealGroups.length > 0 && (
                    <p className="vp-sidebar-subheading">
                      <span>Open</span>
                      <span className="vp-sidebar-subheading-count">{openActiveDealGroups.length}</span>
                    </p>
                  )}
                  <ul className="vp-sidebar-list">
                    {openActiveDealGroups.map(deal => {
                      const timberTableStatus =
                        deal.timber && showTimberTab
                          ? workGroupTableStatus(deal.timber, submissionsByPackage, orgId)
                          : null;
                      const cncTableStatus =
                        deal.cnc && showCncTab
                          ? workGroupTableStatus(deal.cnc, submissionsByPackage, orgId)
                          : null;
                      return renderSidebarDealItem(deal, timberTableStatus, cncTableStatus);
                    })}
                  </ul>
                </>
              )}
              {closedDealGroups.length > 0 && (
                <>
                  <button
                    type="button"
                    className="vp-sidebar-closed-toggle"
                    onClick={() => setClosedProjectsOpen(open => !open)}
                    aria-expanded={closedProjectsOpen}
                  >
                    {closedProjectsOpen ? (
                      <ChevronDown size={13} aria-hidden />
                    ) : (
                      <ChevronRight size={13} aria-hidden />
                    )}
                    <span>Closed Projects</span>
                    <span className="vp-sidebar-closed-count">{closedDealGroups.length}</span>
                  </button>
                  {closedProjectsOpen && (
                  <ul className="vp-sidebar-list">
                    {closedDealGroups.map(deal => {
                      const timberTableStatus =
                        deal.timber && showTimberTab
                          ? workGroupTableStatus(deal.timber, submissionsByPackage, orgId)
                          : null;
                      const cncTableStatus =
                        deal.cnc && showCncTab
                          ? workGroupTableStatus(deal.cnc, submissionsByPackage, orgId)
                          : null;
                      return renderSidebarDealItem(deal, timberTableStatus, cncTableStatus, {
                        lostReason: dealLostReasonTooltip(deal, submissionsByPackage, orgId, {
                          includeTimber: showTimberTab,
                          includeCnc: showCncTab,
                        }),
                      });
                    })}
                  </ul>
                  )}
                </>
              )}
            </aside>

            {selectedDeal && (
              <div className="vp-main">
                {selectedDeal.timber && showTimberTab &&
                  renderStructureWorkPanels('Timber RFQ', selectedDeal.timber, selectedDeal.cnc)}

                {selectedDeal.cnc && showCncTab &&
                  renderStructureWorkPanels('CNC Quote', selectedDeal.cnc, selectedDeal.timber)}
              </div>
            )}
          </div>
        )}

        {dealGroups.length === 0 && orgId && !isEmbedPreview && (
          <p className="vp-muted">No work orders assigned yet. You will receive an email when LC Timber Frames publishes a package.</p>
        )}

        {isEmbedPreview && !publishPreviewPayload && (
          <p className="vp-muted">No preview data loaded. Return to the publish review modal and open the Vendor portal tab.</p>
        )}
      </div>

      {showHelpModal && (
        <VendorPortalHelpModal
          onClose={() => setShowHelpModal(false)}
          onTakeTour={dealGroups.length > 0 ? handleTakeTour : undefined}
          showTimber={showTimberTab}
          showCnc={showCncTab}
        />
      )}

      <SupplierNotBiddingModal
        open={!!notBiddingModal && canSeeFinancials}
        mode={notBiddingModal?.mode ?? 'confirm'}
        busy={!!notBiddingModal?.pkg.id && busyPkg === notBiddingModal.pkg.id}
        onClose={() => setNotBiddingModal(null)}
        onConfirm={handleNotBiddingModalConfirm}
      />

      <SupplierQuoteSubmitModal
        open={!!quoteSubmitPkg && canSeeFinancials}
        busy={!!quoteSubmitPkg?.id && busyPkg === quoteSubmitPkg.id}
        title={
          quoteSubmitPkg && normalizeWorkType(quoteSubmitPkg.workType) === 'cnc_quote'
            ? 'Submit CNC quote'
            : 'Submit timber quote'
        }
        accept={
          quoteSubmitPkg && normalizeWorkType(quoteSubmitPkg.workType) === 'cnc_quote'
            ? '.pdf,application/pdf'
            : '.pdf,.csv,application/pdf,text/csv'
        }
        showDeliveryFields={
          !!quoteSubmitPkg && normalizeWorkType(quoteSubmitPkg.workType) !== 'cnc_quote'
        }
        timberSpeciesOptions={quoteSubmitTimberSpeciesOptions}
        allTimberSpeciesQuoted={quoteSubmitAllTimberSpeciesQuoted}
        structureOptions={quoteSubmitStructureOptions}
        packageScopeStructures={
          quoteSubmitPkg
            ? activeScopeStructures(quoteSubmitPkg.scopeStructures, quoteSubmitPkg.archivedStructureIds)
            : undefined
        }
        lockedStructures={quoteSubmitLockedStructures}
        requestContext={quoteSubmitRequestContext}
        existingTimberStreamKeys={quoteSubmitExistingStreamKeys}
        onClose={() => {
          setQuoteSubmitPkg(null);
          setQuoteSubmitStructure(null);
        }}
        onSubmit={async payload => {
          if (!quoteSubmitPkg?.id) return;
          setBusyPkg(quoteSubmitPkg.id);
          try {
            await handleUpload(quoteSubmitPkg, payload.file, {
              quoteTotal: payload.quoteTotal,
              leadTimeWeeksMin: payload.leadTimeWeeksMin,
              leadTimeWeeksMax: payload.leadTimeWeeksMax,
              notes: payload.notes,
              quotedSpecies: payload.quotedSpecies,
              quotedDryness: payload.quotedDryness,
              quotedStructures: payload.quotedStructures,
            });
            setQuoteSubmitPkg(null);
            setQuoteSubmitStructure(null);
          } catch (err) {
            console.error(err);
            alert(
              err instanceof Error
                ? err.message
                : 'Could not submit quote. Check that your account is linked to this supplier org.',
            );
          } finally {
            setBusyPkg(null);
          }
        }}
      />
      {quotePdfViewer && (
        <VendorQuotePdfModal
          url={quotePdfViewer.url}
          title={quotePdfViewer.title}
          onClose={() => setQuotePdfViewer(null)}
        />
      )}

      <GroupCommentsPopout
        groupContext={commentsContext}
        popoutToken={commentsToken}
        dealId={commentsDealId || undefined}
        highlightCommentId={highlightCommentId}
        onHighlightCommentConsumed={() => setHighlightCommentId(null)}
        onClose={() => {
          setCommentsContext(null);
          setCommentsToken(null);
          setCommentsDealId(null);
          setHighlightCommentId(null);
        }}
      />
    </div>
  );
};

export default VendorPortalPage;
